import { useEffect, useState } from 'react';
import { useT } from '@/lib/i18n';
import { ErrorBanner, Textarea } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** Approve (comment optional), reject (comment required) or ask for approval (note optional). */
export function DecisionDialog({
  open,
  title,
  description,
  confirm,
  required,
  label,
  maxLength = 2000,
  pending,
  error,
  onConfirm,
  onClose,
  destructive = confirm.startsWith('Reject'),
}: {
  open: boolean;
  title: string;
  description: string;
  confirm: string;
  required?: boolean;
  label: string;
  /** The API's limit for the text (2,000 for approvals, 500 for an inbox rejection). */
  maxLength?: number;
  pending?: boolean;
  error?: string | null;
  onConfirm: (text: string) => void;
  onClose: () => void;
  /** A refusal (red button); the English label decides when omitted. */
  destructive?: boolean;
}) {
  const [text, setText] = useState('');
  const t = useT();
  // a fresh box each time: the parent closes it on success without onOpenChange
  useEffect(() => {
    if (open) setText('');
  }, [open]);
  const missing = required && !text.trim();
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setText('');
          onClose();
        }
      }}
    >
      <DialogContent data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <label className="space-y-1 text-sm">
          <span className="font-medium">
            {label}
            {required ? ' *' : ` ${t('decision.optional')}`}
          </span>
          <Textarea
            aria-label={label}
            maxLength={maxLength}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={pending || missing}
            onClick={() => onConfirm(text.trim())}
            variant={destructive ? 'destructive' : 'default'}
          >
            {pending ? t('common.saving') : confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
